"use client";

import { useSyncExternalStore } from "react";

import { getLocalStorageItem, useLocalStorageSubscribe } from "@/hooks/storage/use-local-storage";

import { buildTransferProgressStorageKey } from "./transfer-progress-storage";

/** The localStorage key holding one wallet's transfer list. Owned here so the Toaster can ask
 *  whether there is anything to show without importing the provider — see `useHasTransfers`. */
export function transferProgressStorageKey(ownerWalletAddress: string | undefined) {
  return buildTransferProgressStorageKey(ownerWalletAddress ?? "");
}

/**
 * Whether this wallet has a transfer to show, read straight from storage rather than from
 * `useTransferProgress`.
 *
 * The point is what it does *not* import. The Toaster holds the dynamic boundary into the
 * progress card's chunk, and a module that both holds that boundary and imports
 * `use-transfer-progress` resolves a different `TransferProgressContext` than the provider in a
 * production Turbopack build — every route then dies on hydration, while dev stays clean. Reading
 * the same localStorage the provider writes gets the same answer with no import to trip over.
 *
 * Read-only on purpose: `useLocalStorage` seeds a missing key with its initial value, and the
 * gate has no business writing anything. `useSyncExternalStore` over the same subscription keeps
 * it live, because every write dispatches a `storage` event — so a card raised mid-session
 * mounts the moment `addTransfer` lands, without the provider having to tell us.
 */
export function useHasTransfers(ownerWalletAddress: string | undefined): boolean {
  const raw = useSyncExternalStore(
    useLocalStorageSubscribe,
    () => getLocalStorageItem(transferProgressStorageKey(ownerWalletAddress)),
    () => null,
  );

  if (!raw) return false;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}
